/**
 * Category Summarisation View — proactive Managed Agents v2 dashboard.
 *
 * Renders one card per registered Managed Agent manifest. Cards reflect the
 * live status of the agent's long-lived task row (created automatically the
 * first time a triggering document is indexed). Practitioners cannot start
 * runs manually; they can only re-run a completed agent or cancel one
 * mid-flight.
 */

import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { useActiveOrg } from "@/utils/org-context";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { XCircle, CheckCircle2, Loader2, Clock } from "lucide-react";
import { AgentCard } from "./AgentCard";
import {
  ALL_MANIFEST_UI_METADATA,
  type ManagedAgentUiMetadata,
} from "@cvx/jobs/summarization/agents/uiMetadata";
import { SUMMARIZATION_GROUPS } from "./groups";
import {
  agentIdToSlug,
  isDataAgentVisibleOnJob,
  dataAgentLabelById,
} from "./agentRegistry";
import { caseTypeFromAppointmentType } from "@/lib/precedent-templates";

interface SummarizationViewProps {
  jobId: Id<"jobs">;
  isViewer?: boolean;
  /** Job status — pre-appointment jobs only render the Corporate agent. */
  jobStatus?: string;
  /**
   * Job appointment type — personal-insolvency jobs relabel the Corporate
   * agent card as "Structure". See {@link dataAgentLabelById}.
   */
  appointmentType?: string;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TaskRow = {
  _id: Id<"summarizationTasks">;
  summarizationRunId: Id<"summarizationRuns">;
  agentId: string;
  agentName: string;
  categories: string[];
  status: string;
  startedAt?: number;
  completedAt?: number;
  duration?: number;
  documentCount: number;
  documentsAnalysed: number;
  overallAssessment?: string;
  error?: string;
  retryCount?: number;
  practitionerReviewed: boolean;
  concernCount: number;
  latestActivity?: string;
  workflowStatusId?: Id<"taskStatuses">;
  pendingRerun: boolean;
};

type ManifestSummary = ManagedAgentUiMetadata;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Builds a synthetic AgentCard task row for a manifest that has not yet been
 * triggered (no `summarizationTasks` record exists). The card renders in the
 * "Waiting for documents" state until the first matching document is indexed.
 */
function buildPlaceholderTask(manifest: ManifestSummary): {
  agentId: string;
  agentName: string;
  categories: string[];
  status: string;
  documentCount: number;
  documentsAnalysed: number;
  concernCount: number;
} {
  return {
    agentId: manifest.id,
    agentName: manifest.name,
    categories: manifest.triggerCategories,
    status: "waiting",
    documentCount: 0,
    documentsAnalysed: 0,
    concernCount: 0,
  };
}

/**
 * Agents that have been re-homed under Investigations. They still exist as
 * Convex manifests and run normally — they just no longer surface as cards
 * on the Data overview. (Asset Sales & Purchases now lives in the Data
 * section, so it is no longer hidden here.)
 */
const AGENTS_HIDDEN_FROM_DATA_OVERVIEW = new Set<string>([
  "sum_financial_analysis",
]);

// Frontend-safe manifest summaries (no Anthropic SDK / process.env imports).
// Source of truth: convex/jobs/summarization/agents/uiMetadata.ts
const MANIFEST_SUMMARIES: ReadonlyArray<ManifestSummary> =
  ALL_MANIFEST_UI_METADATA.filter(
    (m) => !AGENTS_HIDDEN_FROM_DATA_OVERVIEW.has(m.id),
  );

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SummarizationView({
  jobId,
  isViewer,
  jobStatus,
  appointmentType,
}: SummarizationViewProps) {
  const navigate = useNavigate();
  // Personal-insolvency jobs relabel the Corporate agent ("Structure").
  const isBankruptcy =
    caseTypeFromAppointmentType(appointmentType) === "personal";

  /**
   * Open an agent's detail page via slug route. Falls back to no-op for
   * agents not present in the registry (shouldn't happen in practice but
   * guards against drift between the manifest list and the registry).
   */
  const openAgentBySlug = (agentId: string) => {
    const slug = agentIdToSlug(agentId);
    if (!slug) return;
    navigate({
      to: "/dashboard/jobs/$jobId/summaries/$agent",
      params: { jobId, agent: slug },
    });
  };

  const { data: tasksData } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTasksByJob,
      { jobId },
    ),
  );

  const tasks: ReadonlyArray<TaskRow> = (tasksData ??
    []) as ReadonlyArray<TaskRow>;
  const tasksByAgentId = new Map<string, TaskRow>();
  for (const task of tasks) tasksByAgentId.set(task.agentId, task);

  const visibleManifests = MANIFEST_SUMMARIES.filter((manifest) =>
    isDataAgentVisibleOnJob(manifest.id, jobStatus, appointmentType),
  );

  // Aggregate progress counts across the visible manifests so the practitioner
  // gets a snapshot in the header without an extra Convex query.
  const progress = visibleManifests.reduce(
    (acc, manifest) => {
      const status = tasksByAgentId.get(manifest.id)?.status ?? "waiting";
      acc.total += 1;
      if (status === "completed") acc.completed += 1;
      else if (status === "running") acc.running += 1;
      else if (status === "failed") acc.failed += 1;
      else if (status === "waiting") acc.waiting += 1;
      else if (status === "cancelled") acc.cancelled += 1;
      return acc;
    },
    { total: 0, completed: 0, running: 0, failed: 0, waiting: 0, cancelled: 0 },
  );

  // Mutations — only manual overrides remain. Runs are otherwise event-driven.
  const { mutateAsync: cancelSummarization } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.summarization.actions.cancelSummarization,
    ),
  });
  const { mutateAsync: rerunAgent } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.summarization.actions.rerunSummarizationAgent,
    ),
  });
  const { mutateAsync: setAgentWorkflowStatus } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.taskState.setWorkflowStatus,
    ),
  });

  const { orgId } = useActiveOrg();

  const handleWorkflowStatusChange = async (
    taskId: Id<"summarizationTasks">,
    statusId: Id<"taskStatuses"> | null,
  ) => {
    try {
      await setAgentWorkflowStatus({ taskId, statusId });
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to update workflow status",
      );
    }
  };

  const handleRerunAgent = async (taskId: Id<"summarizationTasks">) => {
    try {
      await rerunAgent({ taskId, jobId });
      toast.success("Agent re-running");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to re-run agent");
    }
  };

  const handleCancelAgent = async (taskId: Id<"summarizationTasks">) => {
    try {
      await cancelSummarization({ taskId, jobId });
      toast.success("Agent cancelled");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to cancel agent");
    }
  };

  // Detail view is now rendered by the /summaries/$agent slug route, not
  // in-page. Card "Open" buttons navigate via openAgentBySlug.

  return (
    <div className="-m-6 flex flex-1 flex-col gap-5 bg-muted px-6 pb-6 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold">Data</h3>
            <div className="mt-1 flex items-center gap-3">
              {progress.completed > 0 && (
                <span className="flex items-center gap-1 text-xs text-emerald-600">
                  <CheckCircle2 className="h-3 w-3" />
                  {progress.completed} completed
                </span>
              )}
              {progress.running > 0 && (
                <span className="flex items-center gap-1 text-xs text-blue-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {progress.running} running
                </span>
              )}
              {progress.waiting > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {progress.waiting} waiting
                </span>
              )}
              {progress.failed > 0 && (
                <span className="flex items-center gap-1 text-xs text-destructive">
                  <XCircle className="h-3 w-3" />
                  {progress.failed} failed
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grouped agent list */}
      <div className="flex flex-col gap-5">
        {SUMMARIZATION_GROUPS.map((group) => {
          const groupManifests = visibleManifests.filter(
            (m) => m.group === group.id,
          );
          if (groupManifests.length === 0) return null;

          const independent = groupManifests.filter(
            (m) => !m.dependsOn?.length,
          );
          const dependent = groupManifests.filter(
            (m) => (m.dependsOn?.length ?? 0) > 0,
          );

          const completedCount = groupManifests.reduce((n, m) => {
            const status = tasksByAgentId.get(m.id)?.status ?? "waiting";
            return status === "completed" ? n + 1 : n;
          }, 0);

          return (
            <div
              key={group.id}
              className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]"
            >
              {/* Group header */}
              <div className="flex items-baseline justify-between px-5 py-3">
                <h4 className="text-sm font-semibold">{group.label}</h4>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {completedCount}/{groupManifests.length} completed
                </span>
              </div>

              {/* Independent agents */}
              <div className="divide-y divide-border">
                {independent.map((manifest) =>
                  renderManifestCard({
                    manifest,
                    task: tasksByAgentId.get(manifest.id),
                    isViewer,
                    isBankruptcy,
                    orgId: (orgId as Id<"organizations">) ?? null,
                    onOpen: openAgentBySlug,
                    onRerun: handleRerunAgent,
                    onCancel: handleCancelAgent,
                    onWorkflowStatusChange: handleWorkflowStatusChange,
                  }),
                )}
              </div>

              {/* Dependent agents */}
              {dependent.length > 0 && (
                <>
                  <div className="border-t border-border px-5 py-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Dependant Summary
                    </span>
                  </div>
                  <div className="divide-y divide-border">
                    {dependent.map((manifest) =>
                      renderManifestCard({
                        manifest,
                        task: tasksByAgentId.get(manifest.id),
                        isViewer,
                        isBankruptcy,
                        orgId: (orgId as Id<"organizations">) ?? null,
                        onOpen: openAgentBySlug,
                        onRerun: handleRerunAgent,
                        onCancel: handleCancelAgent,
                        onWorkflowStatusChange: handleWorkflowStatusChange,
                      }),
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

function renderManifestCard(args: {
  manifest: ManifestSummary;
  task: TaskRow | undefined;
  isViewer: boolean | undefined;
  isBankruptcy: boolean;
  orgId: Id<"organizations"> | null;
  onOpen: (agentId: string) => void;
  onRerun: (taskId: Id<"summarizationTasks">) => void;
  onCancel: (taskId: Id<"summarizationTasks">) => void;
  onWorkflowStatusChange: (
    taskId: Id<"summarizationTasks">,
    statusId: Id<"taskStatuses"> | null,
  ) => void;
}) {
  const {
    manifest,
    task,
    isViewer,
    isBankruptcy,
    orgId,
    onOpen,
    onRerun,
    onCancel,
    onWorkflowStatusChange,
  } = args;

  // Regime-aware display name — the Corporate agent reads as "Structure" on a
  // personal-insolvency job. Falls back to the manifest name for every other
  // agent (and all corporate appointments).
  const displayName =
    dataAgentLabelById(manifest.id, isBankruptcy) ?? manifest.name;

  if (task) {
    return (
      <AgentCard
        key={manifest.id}
        task={{
          ...task,
          // Manifest is the source of truth for the display label. Existing
          // task rows in the DB may have been written before manifest renames
          // (e.g. "Corporate Summary" → "Corporate"); override here so the
          // UI updates immediately without waiting for tasks to be re-created.
          agentName: displayName,
          categories: manifest.triggerCategories,
        }}
        uiDescription={manifest.uiDescription}
        isViewer={isViewer}
        orgId={orgId}
        onWorkflowStatusChange={(statusId) =>
          onWorkflowStatusChange(task._id, statusId)
        }
        onSelect={() => onOpen(manifest.id)}
        onRerun={() => onRerun(task._id)}
        onCancel={() => onCancel(task._id)}
        grouped
      />
    );
  }

  // No task row yet — show the proactive placeholder. Card stays openable so
  // the user can land on the agent page even before its first run; the slug
  // route handles the empty state.
  return (
    <AgentCard
      key={manifest.id}
      task={{ ...buildPlaceholderTask(manifest), agentName: displayName }}
      uiDescription={manifest.uiDescription}
      isViewer={isViewer}
      onSelect={() => onOpen(manifest.id)}
      grouped
    />
  );
}
